import { motion } from "framer-motion";
import { BookOpen, Sun, Moon, Sparkles, Crown, LogOut, Timer, HelpCircle, Gamepad2, Trash2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { InstallPWA } from "@/components/InstallPWA";
import { AvatarPill } from "@/components/AvatarPicker";

const TABS = [
  { id: "create", label: "Create" },
  { id: "dashboard", label: "Dashboard" },
  { id: "lecture", label: "Lecture", needsLecture: true },
  { id: "guide", label: "Guide", needsLecture: true },
  { id: "quiz", label: "Quiz", needsLecture: true },
  { id: "bilingual", label: "Bilingual", needsLecture: true, bilingualOnly: true },
  { id: "classes", label: "Classes" },
  { id: "exam", label: "Exam Prep" },
  { id: "instructor", label: "Instructor", instructorOnly: true },
  { id: "library", label: "My Library" },
  { id: "explore", label: "Explore" },
];

export const Header = ({ active, onNav, dark, onToggleTheme, hasLecture, canBilingual, user, onUpgrade, onLogout, onQuickStudy, onManagePlan, onHelp, onAvatar, onDeleteAccount }) => {
  const isPro = user?.plan === "pro";
  const isInstructor = user?.role === "admin" || user?.role === "instructor";
  const visibleTabs = TABS.filter((t) => (!t.instructorOnly || isInstructor) && (!t.bilingualOnly || canBilingual));
  return (
    <header className="no-print sticky top-0 z-40 backdrop-blur-md bg-background/80 border-b border-border/60">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16 gap-4">
        <button data-testid="brand-logo" onClick={() => onNav("create")} className="flex items-center gap-2.5 shrink-0">
          <span className="grid place-items-center h-9 w-9 rounded-xl bg-primary text-primary-foreground">
            <BookOpen className="h-5 w-5" />
          </span>
          <span className="font-serif text-2xl font-bold tracking-tight leading-none">
            Lectura<span className="text-accent">.</span>
          </span>
        </button>

        <nav className="hidden lg:flex items-center gap-1 bg-secondary/60 rounded-full p-1">
          {visibleTabs.map((t) => {
            const disabled = !hasLecture && t.needsLecture;
            return (
              <button
                key={t.id}
                data-testid={`nav-${t.id}`}
                disabled={disabled}
                onClick={() => onNav(t.id)}
                className={cn(
                  "relative px-3.5 py-1.5 rounded-full text-sm font-semibold transition-colors",
                  disabled && "opacity-40 cursor-not-allowed",
                  active === t.id ? "text-primary-foreground" : "text-muted-foreground hover:text-foreground"
                )}
              >
                {active === t.id && (
                  <motion.span layoutId="nav-pill" className="absolute inset-0 rounded-full bg-primary"
                    transition={{ type: "spring", stiffness: 400, damping: 32 }} />
                )}
                <span className="relative z-10">{t.label}</span>
              </button>
            );
          })}
        </nav>

        <div className="flex items-center gap-2 shrink-0">
          <InstallPWA />
          <AvatarPill onClick={onAvatar} />
          <button
            data-testid="join-live-btn"
            onClick={() => (window.location.href = "/join-game")}
            className="hidden sm:flex items-center gap-1.5 px-3 py-2 rounded-full border border-purple-500/40 bg-purple-500/10 text-purple-700 dark:text-purple-300 text-xs font-bold uppercase tracking-widest hover:bg-purple-500/20 transition"
            title="Join a live class game"
          >
            <Gamepad2 className="h-4 w-4" /> Live
          </button>
          <button
            data-testid="quickstudy-btn"
            onClick={onQuickStudy}
            className="hidden sm:flex items-center gap-1.5 px-3 py-2 rounded-full border border-border text-sm font-semibold hover:border-accent/50 transition"
          >
            <Timer className="h-4 w-4 text-accent" /> 10-min
          </button>
          <button data-testid="help-tour-btn" onClick={onHelp} title="How it works"
            className="grid place-items-center h-9 w-9 rounded-full border border-border hover:border-primary/50 transition">
            <HelpCircle className="h-4 w-4" />
          </button>
          <button data-testid="theme-toggle" onClick={onToggleTheme}
            className="grid place-items-center h-9 w-9 rounded-full border border-border hover:border-primary/50 transition" aria-label="Toggle theme">
            {dark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          </button>

          {isPro ? (
            <button data-testid="manage-plan-btn" onClick={onManagePlan}
              className="hidden sm:flex items-center gap-1.5 rounded-full bg-accent/15 text-accent px-3 py-1.5 text-xs font-bold hover:bg-accent/25 transition active:scale-95">
              <Crown className="h-3.5 w-3.5" /> PRO
            </button>
          ) : (
            <button data-testid="header-upgrade-btn" onClick={onUpgrade}
              className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-accent text-accent-foreground text-sm font-semibold hover:opacity-90 transition active:scale-95">
              <Crown className="h-4 w-4" /> Upgrade
            </button>
          )}

          <button data-testid="logout-btn" onClick={onLogout} title="Log out"
            className="grid place-items-center h-9 w-9 rounded-full border border-border hover:border-destructive/50 hover:text-destructive transition">
            <LogOut className="h-4 w-4" />
          </button>
          {onDeleteAccount && (
            <button
              data-testid="header-delete-account-btn"
              onClick={onDeleteAccount}
              title="Delete my account & data"
              className="hidden lg:grid place-items-center h-9 w-9 rounded-full border border-border text-muted-foreground hover:border-destructive/50 hover:text-destructive transition"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>

      {/* mobile nav */}
      <div className="lg:hidden overflow-x-auto no-scrollbar border-t border-border/60">
        <div className="flex gap-1 px-4 py-2 min-w-max">
          {visibleTabs.map((t) => {
            const disabled = !hasLecture && t.needsLecture;
            return (
              <button key={t.id} data-testid={`mnav-${t.id}`} disabled={disabled} onClick={() => onNav(t.id)}
                className={cn("px-3 py-1.5 rounded-full text-sm font-semibold whitespace-nowrap",
                  disabled && "opacity-40",
                  active === t.id ? "bg-primary text-primary-foreground" : "text-muted-foreground")}>
                {t.label}
              </button>
            );
          })}
        </div>
      </div>
    </header>
  );
};
